import { App, AppStatusError, AuthenticationError, AuthorizationError, NetworkError, NotFoundError, Plan, Subscription, User } from "@back4app2/sdk";
import { useEffect, useReducer, useState, useCallback } from "react";
import { useLocation, useNavigate, useOutletContext, useParams } from "react-router-dom";
import back4app2 from "../back4app2";
import { hasReadPermission, hasWritePermission } from "../utils/permissions";
import LoadingSpinner from "../components/LoadingSpinner";
import ErrorMessage from "../components/ErrorMessage";
import TextInput from "../components/FormInputField/TextInput/TextInput";
import { BACK4APP_DOT_COM_SITE_URL, BACK4APP_DOT_COM_DASHBOARD_URL } from "../settings";
import ToggleSwitchCheckbox from "../components/FormInputField/ToggleSwitchCheckbox/ToggleSwitchCheckbox";
import toast from "react-hot-toast";
import { ReactComponent as PlusIcon } from '../assets/images/plus-icon.svg';
import { ReactComponent as TrashIcon } from "../assets/images/trash-icon.svg";
import { ReactComponent as VisibilityIcon } from "../assets/images/visibility-icon.svg";
import { ReactComponent as VisibilityOffIcon } from "../assets/images/visibility-off-icon.svg";
import { ReactComponent as TrashOutlineIcon } from "../assets/images/trash-outline-icon.svg";
import { ReactComponent as TriangleExclamationIcon } from '../assets/images/triangle-exclamation.svg';
import { ReactComponent as AttentionIconSVG } from "../assets/images/circle-exclaimation-icon.svg";
import { ReactComponent as StatusErrorSVG } from '../assets/images/status-error.svg';
import { ReactComponent as StatusSuccessSVG } from '../assets/images/status-success.svg';
import { ReactComponent as StatusSpinnerSVG } from '../assets/images/status-spinner.svg';
import { ReactComponent as GhostIconSVG } from "../assets/images/ghost-icon.svg";
import { ReactComponent as CopySVG } from '../assets/images/copy-icon.svg';
import Button from "../components/Button";
import Modal from "../components/Modal";
import CustomDomainSettings from "../components/CustomDomainSettings";
import ReactTooltip from "react-tooltip";
import { AmplitudeEvent, trackEvent } from "../utils/amplitude";
import { initializePaddle, InitializePaddleOptions } from '@paddle/paddle-js';
import Collaborators from "../components/Collaborators/Collaborators";
import GhostMessage from "../components/GhostMessage";

enum MyAppSettingsTab {
  GENERAL_SETTINGS = 'general-settings',
  BUILD_AND_DEPLOY = 'build-and-deploy',
  VARIABLES = 'variable',
  CUSTOM_DOMAIN = 'custom-domain',
  HEALTH_AND_ALERT = 'health-and-alert',
  SUSPEND_DELETE = 'suspend-delete'
}

enum MyAppSettingsFieldName {
  BRANCH_NAME,
  ROOT_DIR,
  AUTO_DEPLOY,
  ENV_VARS,
  EXPOSED_PORT,
  HEALTH_CHECK_ENDPOINT,
  COLLABORATOR_EMAIL
}

interface MyAppSettingsState {
  currentTab: MyAppSettingsTab;
  isLoadingApp: boolean;
  appLoadingErrorMessage?: string;
  app?: App;
  isSubmitting: boolean;
  submittingErrorMessage?: string;
  isTriggerDeploymentModalOpen: boolean;
  plans?: Plan[];
  isLoadingPlans: boolean;
  loadingPlansErrorMessage?: string;
  // Paddle-related state
  isLoadingPaddle: boolean;
  paddleError?: string;
  paddle?: any;
  openCheckout: boolean;
  selectedPlan?: Plan;
}

const INITIAL_STATE: MyAppSettingsState = {
  currentTab: MyAppSettingsTab.GENERAL_SETTINGS,
  isLoadingApp: true,
  isSubmitting: false,
  isTriggerDeploymentModalOpen: false,
  isLoadingPlans: true,
  // Paddle-related initial state
  isLoadingPaddle: false,
  openCheckout: false,
};

enum MyAppSettingsActionType {
  RESET,
  FINISH_LOADING_APP,
  SET_CURRENT_TAB,
  SET_TRIGGER_DEPLOYMENT_MODAL,
  FINISH_UPDATING_APP,
  FINISH_LOADING_PLANS,
  // Paddle-related actions
  SET_PADDLE_LOADING,
  FINISH_LOADING_PADDLE,
  SET_CHECKOUT_MODAL,
  SET_SELECTED_PLAN,
}

const reset = () => ({
  type: MyAppSettingsActionType.RESET
} as const);

const setCurrentTab = (tab: MyAppSettingsTab) => ({
  type: MyAppSettingsActionType.SET_CURRENT_TAB,
  payload: tab
} as const);

const finishLoadingApp = (errorMessage?: string, app?: App) => ({
  type: MyAppSettingsActionType.FINISH_LOADING_APP,
  payload: {
    errorMessage,
    app
  }
} as const);

const finishUpdatingApp = (app: App, errorMessage?: string) => ({
  type: MyAppSettingsActionType.FINISH_UPDATING_APP,
  payload: {
    errorMessage,
    app
  }
} as const);

const setPaddleLoading = (isLoading: boolean) => ({
  type: MyAppSettingsActionType.SET_PADDLE_LOADING,
  payload: isLoading,
} as const);

const finishLoadingPlans = (errorMessage?: string, plans?: Plan[]) => ({
  type: MyAppSettingsActionType.FINISH_LOADING_PLANS,
  payload: {
    errorMessage,
    plans
  }
} as const);

const openTriggerDeploymentModal = () => ({
  type: MyAppSettingsActionType.SET_TRIGGER_DEPLOYMENT_MODAL,
  payload: true,
} as const);

const closeTriggerDeploymentModal = () => ({
  type: MyAppSettingsActionType.SET_TRIGGER_DEPLOYMENT_MODAL,
  payload: false,
} as const);

// Paddle-related action creators
const finishLoadingPaddle = (errorMessage?: string, paddle?: any) => ({
  type: MyAppSettingsActionType.FINISH_LOADING_PADDLE,
  payload: {
    errorMessage,
    paddle,
  },
} as const);

const setCheckoutModal = (isOpen: boolean) => ({
  type: MyAppSettingsActionType.SET_CHECKOUT_MODAL,
  payload: isOpen,
} as const);

const setSelectedPlan = (plan?: Plan) => ({
  type: MyAppSettingsActionType.SET_SELECTED_PLAN,
  payload: plan,
} as const);

type MyAppSettingsAction = ReturnType<typeof reset> | ReturnType<typeof finishLoadingApp> | ReturnType<typeof setCurrentTab> | ReturnType<typeof openTriggerDeploymentModal> | ReturnType<typeof closeTriggerDeploymentModal> | ReturnType<typeof finishUpdatingApp> | ReturnType<typeof finishLoadingPlans> | ReturnType<typeof setPaddleLoading> | ReturnType<typeof finishLoadingPaddle> | ReturnType<typeof setCheckoutModal> | ReturnType<typeof setSelectedPlan>;

const reducer = (state: MyAppSettingsState = INITIAL_STATE, action: MyAppSettingsAction): MyAppSettingsState => {
  switch (action.type) {
    case MyAppSettingsActionType.RESET:
      return INITIAL_STATE;
    
    case MyAppSettingsActionType.FINISH_LOADING_APP:
      return {
        ...state,
        isLoadingApp: false,
        appLoadingErrorMessage: action.payload.errorMessage,
        app: action.payload.app
      }
    case MyAppSettingsActionType.SET_CURRENT_TAB: {
      return {
        ...state,
        currentTab: action.payload || MyAppSettingsTab.GENERAL_SETTINGS
      }
    }
    case MyAppSettingsActionType.SET_TRIGGER_DEPLOYMENT_MODAL:
      return {
        ...state,
        isTriggerDeploymentModalOpen: action.payload
      }
    case MyAppSettingsActionType.FINISH_UPDATING_APP:
      return {
        ...state,
        app: action.payload.app,
        isSubmitting: false,
        submittingErrorMessage: action.payload.errorMessage
      }
    case MyAppSettingsActionType.FINISH_LOADING_PLANS:
      return {
        ...state,
        plans: action.payload.plans,
        isLoadingPlans: false,
        loadingPlansErrorMessage: action.payload.errorMessage
      }
    case MyAppSettingsActionType.SET_PADDLE_LOADING:
      return {
        ...state,
        isLoadingPaddle: action.payload
      }
    case MyAppSettingsActionType.FINISH_LOADING_PADDLE:
      return {
        ...state,
        paddle: action.payload.paddle,
        paddleError: action.payload.errorMessage,
        isLoadingPaddle: false,
      }
    case MyAppSettingsActionType.SET_CHECKOUT_MODAL:
      return {
        ...state,
        openCheckout: action.payload
      }
    case MyAppSettingsActionType.SET_SELECTED_PLAN:
      return {
        ...state,
        selectedPlan: action.payload
      }
  }
};

type AppForm = {
  branchName?: string, 
  rootDir?: string, 
  envs?: { key: string; value?: string }[], 
  autoDeploy?: boolean,
  exposedPort?: number,
  healthCheckEndpoint?: string
}

const MyAppSettings = () => {
  const { appId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { updateIsDeletingApp, user } = useOutletContext<{ updateIsDeletingApp: (isDeleting: boolean) => void, user: User }>();
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);
  const [isFreePlan, setisFreePlan] = useState(true); // Default to true to be safe
  const { currentTab, app, isLoadingApp, appLoadingErrorMessage, isSubmitting, isTriggerDeploymentModalOpen, plans, isLoadingPlans, loadingPlansErrorMessage, paddle, openCheckout } = state;
  const mainServiceEnvironment = app && app.mainService && app.mainService.mainServiceEnvironment;
  const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;

  useEffect(() => {
    dispatch(setCurrentTab(location.hash.replace('#', '') as MyAppSettingsTab));
  }, [location.hash]);

  useEffect(() => {
    let appSubscription: Subscription;
    (async() => {

      if (appId) {
        try {
          const app: App = await back4app2.findAppById(appId);
          dispatch(finishLoadingApp(undefined, app));
        } catch (err) {
          let errMsg = '';
          if (err instanceof NetworkError) {
            console.error('network error', err);
            errMsg = 'Network error when fetching app settings. Check your internet connection and try again.';
          } else if (err instanceof NotFoundError) {
            errMsg = 'App not found!'
          } else {
            console.error('unexpected error fetching app settings', err);
            errMsg = 'Unexpected error while fetching app settings. Please try again.'
          }
          dispatch(finishLoadingApp(errMsg, undefined));
        }

        appSubscription = back4app2.subscribeToApp(appId, (error, snapshot) => {
          if (error) {
            setisFreePlan(true);
          } else if (snapshot) {
            // Check if the app is on a free plan
            const mainServiceEnvironment = snapshot.mainService && snapshot.mainService.mainServiceEnvironment;
            const isPendingPayment = mainServiceEnvironment && (
              mainServiceEnvironment.isPendingPayment || 
              (!mainServiceEnvironment.isFreePlanElegible && mainServiceEnvironment.plan.name.includes('Free'))
            );
            const isFree = mainServiceEnvironment?.plan.name.includes('Free') || false;
            setisFreePlan(isFree || !!isPendingPayment);
          }
        });
      }
    })();

    return () => {
      if (appSubscription) {
        appSubscription.unsubscribe();
      }
    }
  }, [appId]);

  useEffect(() => {
    (async() => {
      try {
        const plans = await back4app2.getPlans();
        dispatch(finishLoadingPlans(undefined, plans));
      } catch (err) {
        let errMsg = '';
        if (err instanceof NetworkError) {
          console.error('network error', err);
          errMsg = 'Network error when fetching plans. Check your internet connection and try again.';
        } else {
          console.error('unexpected error fetching plans', err);
          errMsg = 'Unexpected error while fetching plans. Please try again.'
        }
        dispatch(finishLoadingPlans(errMsg));
      }
    })();
  }, []);

  const loadPaddle = useCallback(() => {
    const paddleOptions = {
      token: process.env.REACT_APP_PADDLE_TOKEN || 'test_0270ab179b4f4abd7aa228c7014',
      environment: process.env.REACT_APP_SENTRY_ENV === 'production' ? 'production' : 'sandbox',
      pwCustomer: {}
    };

    const paddleEventCallback = async function (data: any) {
      if (data.name === 'checkout.completed') {
        const paymentData = {
          appId: data.data.custom_data.app_id,
          customerId: data.data.customer.id,
          planName: data.data.items[0].product.name,
          transactionId: data.data.transaction_id,
          checkoutId: data.data.id,
          results: data.data,
          planId: data.data.custom_data.plan_id,
          email: data.data.customer.email,
        };

        // Save subscription
        try {
          await fetch(`${process.env.REACT_APP_BACK4APP_CHECKOUT_URL}/save-subscription`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(paymentData),
          });
        } catch (error) {
          console.error('Error saving subscription:', error);
          toast.error('Error updating plan information, please contact support!');
        }

        await new Promise(resolve => setTimeout(resolve, 1000));

        dispatch(setCheckoutModal(false));
        dispatch(setSelectedPlan(undefined));

        toast.success('Plan updated successfully!', {
          className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
          icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 6000
        });

        await new Promise(resolve => setTimeout(resolve, 1000));

        navigate(0);
      }
      if (data.name === 'checkout.closed') {
        dispatch(setCheckoutModal(false));
        dispatch(setSelectedPlan(undefined));
      }
    };

    initializePaddle({ ...paddleOptions, eventCallback: paddleEventCallback } as InitializePaddleOptions).then(
      (paddleInstance: any) => {
        if (paddleInstance) {
          dispatch(finishLoadingPaddle(undefined, paddleInstance));
        }
      },
    ).catch((error) => {
      console.error('Paddle initialization failed:', error);
      dispatch(finishLoadingPaddle('Failed to initialize Paddle', undefined));
      toast.error('Failed to open checkout, please contact support!', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
    });
  }, [navigate]);

  // Paddle initialization
  useEffect(() => {
    loadPaddle();
  }, [loadPaddle]);

  const handleOnClickPlan = async (plan: Plan) => {
    if (plan.name === 'Free') {
      toast.error('Please contact support for Free plan!', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <AttentionIconSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      return;
    }
    dispatch(setSelectedPlan(plan));
    dispatch(setCheckoutModal(true));

    // Wait for the modal to be rendered before opening Paddle checkout
    setTimeout(async () => {
      if (paddle) {
        const paddlePriceId = process.env.REACT_APP_SENTRY_ENV === 'production' ? plan.checkoutPriceKey : 'pri_01jj29jwpxef927zy0waqcdtjh';

        let userEmail = user.username;

        if (app && !app.isOwner) {
          userEmail = await back4app2.getAppOwnerEmail(app.id);
        }
        
        try {
          paddle.Checkout.open({
            items: [{ priceId: paddlePriceId, quantity: 1 }],
            title: plan.name,
            settings: {
              displayMode: 'inline',
              theme: 'light',
              locale: 'en',
              variant: 'one-page',
              frameTarget: 'checkout-container',
              frameInitialHeight: '450',
              frameStyle: 'width: 100%; min-width: 312px; max-height: 80vh; background-color: #f9f9f9; border: none;'
            },
            customData: { appId: appId, planId: plan.checkoutPlanId },
            allowLogout: false,
            customer: {
              email: userEmail,
            }
          });
        } catch (error) {
          console.error('Error opening Paddle checkout inline:', error);
          try {
            paddle.Checkout.open({
              items: [{ priceId: paddlePriceId, quantity: 1 }],
              title: plan.name,
              customData: { appId: appId, planId: plan.checkoutPlanId },
              allowLogout: false,
              customer: {
                email: user.username,
              }
            });
          } catch (fallbackError) {
            console.error('Fallback Paddle checkout also failed:', fallbackError);
          }
        }
      } else {
        console.error('Paddle instance not available');
      }
    }, 200); // Increased delay to ensure modal is fully rendered
  };

  const handleSubmit = async (newValues: AppForm) => {
    // check if something change
    let isSettingsUpdated = false;
    if (newValues.branchName && newValues.branchName !== app?.mainService?.mainServiceEnvironment?.branchName) {
      isSettingsUpdated = true;
    }
    if (newValues.rootDir && newValues.rootDir !== app?.mainService?.mainServiceEnvironment?.rootDirPath) {
      isSettingsUpdated = true;
    }
    if (typeof newValues.autoDeploy !== "undefined" && newValues.autoDeploy !== app?.mainService?.mainServiceEnvironment?.autoDeploy) {
      isSettingsUpdated = true;
    }
    if (newValues.envs) {
      if (app?.mainService?.mainServiceEnvironment?.envVars.length !== newValues.envs.length) {
        isSettingsUpdated = true;
      } else {
        // check each envs
        for (let idx = 0; idx < newValues.envs.length; idx++) {
          const newEnv = newValues.envs[idx];
          const sameEnv = app.mainService.mainServiceEnvironment.envVars.find(oldEnv => {
            if (oldEnv.key === newEnv.key && oldEnv.value === newEnv.value) return false;
            else if (oldEnv.key === newEnv.key && oldEnv.value !== newEnv.value) return true;
            else if (oldEnv.key !== newEnv.key && oldEnv.value === newEnv.value) return true;
            return false;
          });
          if (sameEnv) {
            isSettingsUpdated = true; break;
          }
        }
      }
    }
    if (newValues.exposedPort !== app?.mainService?.mainServiceEnvironment?.exposedPort) {
      isSettingsUpdated = true;
    }
    if (newValues.healthCheckEndpoint && newValues.healthCheckEndpoint !== app?.mainService?.mainServiceEnvironment?.healthCheckEndpoint) {
      isSettingsUpdated = true;
    }

    if (!isSettingsUpdated) {
      toast('Please make some changes to save new settings!', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-md text-sm",
        icon: <AttentionIconSVG width="20px" height="20px" className="animate-bounce-in text-old-blue" />
      });
      return;
    }

    const toastId = toast.loading('Updating settings...', {
      className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
      icon: <StatusSpinnerSVG width="24px" height="24px" color="text-old-blue" className="animate-spin" />
    });
    try {
      const updatedApp = await back4app2.updateAppSettings(appId as string, newValues.branchName, newValues.rootDir, newValues.autoDeploy, newValues.envs, newValues.healthCheckEndpoint, newValues.exposedPort);
      dispatch(finishUpdatingApp(updatedApp, undefined));
      toast.success('Settings were updated!', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        id: toastId,
        icon: <StatusSuccessSVG width="24px" height="24px" className="animate-bounce-in top-" />
      });
      // Only show deployment modal if user has write permission for Deploy
      if (updatedApp && hasWritePermission(updatedApp, 'Deploy')) {
        dispatch(openTriggerDeploymentModal());
      }
    } catch (err) {
      let errMsg = 'Something went wrong!';
      if (err instanceof AppStatusError || err instanceof AuthorizationError) {
        errMsg = err.message;
      } else {
        console.error('error', err);
      }
      toast.error(errMsg, {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        id: toastId,
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
      //
    }
  }

  const getFormContent = () => {
    let headerText = '';
    let formContent;
    if (!app) {
      return;
    }
    switch (currentTab) {
      case MyAppSettingsTab.GENERAL_SETTINGS: {
        if (!hasReadPermission(app, 'General')) {
          return null;
        }
        headerText = 'General Settings';
        formContent = <GeneralSettingsForm currentUser={user} appId={app.id} appName={app.name} plan={app.mainService?.mainServiceEnvironment?.plan} plans={plans} isLoadingPlans={isLoadingPlans} loadingPlansErrorMessage={loadingPlansErrorMessage} onPlanClick={handleOnClickPlan} isOwner={app.isOwner} />;
        break;
      }
      case MyAppSettingsTab.BUILD_AND_DEPLOY: {
        headerText = 'Build & Deploy';
        formContent = <BuildAndDeployForm 
          branchName={app.mainService?.mainServiceEnvironment?.branchName || ''} 
          rootDir={app.mainService?.mainServiceEnvironment?.rootDirPath || ''} 
          autoDeploy={!!app.mainService?.mainServiceEnvironment?.autoDeploy}
          exposedPort={`${app.mainService?.mainServiceEnvironment?.exposedPort || ''}`}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          plan={app.mainService?.mainServiceEnvironment?.plan}
          isFreePlan={isFreePlan}
          canWrite={hasWritePermission(app, 'BuildAndDeploy')}
          canRead={hasReadPermission(app, 'BuildAndDeploy')}
        />;
        break;
      }
      case MyAppSettingsTab.VARIABLES: {
        headerText = 'Environment Variables';
        formContent = <VariablesForm envVars={app.mainService?.mainServiceEnvironment?.envVars || []} isSubmitting={isSubmitting} handleSubmit={handleSubmit} canWrite={hasWritePermission(app, 'EnvVars')} canRead={hasReadPermission(app, 'EnvVars')} />;
        break;
      }
      case MyAppSettingsTab.CUSTOM_DOMAIN: {
        headerText = 'Domains';
        formContent = <CustomDomainSettings 
          appId={app.id}
          serviceEnvironmentId={mainServiceEnvironmentId as string} 
          mainCustomDomainId={app.mainService?.mainServiceEnvironment?.mainCustomDomain?.id as string}
          onClickUpgrade={() => dispatch(setCurrentTab(MyAppSettingsTab.GENERAL_SETTINGS))}
          canRead={hasReadPermission(app, 'Domain')}
          canWrite={hasWritePermission(app, 'Domain')}
          canDeploy={hasWritePermission(app, 'Deploy')}
        />
        break;
      }
      case MyAppSettingsTab.HEALTH_AND_ALERT: {
        headerText = 'Health and Alert';
        formContent = <HealthAndAlertForm healthCheckEndpoint={app.mainService?.mainServiceEnvironment?.healthCheckEndpoint || ''}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          canWrite={hasWritePermission(app, 'HealthAndAlert')}
          canRead={hasReadPermission(app, 'HealthAndAlert')}
        />
        break;
      }
      case MyAppSettingsTab.SUSPEND_DELETE: {
        headerText = 'Delete';
        formContent = <SuspendDeleteForm appName={app.name} appId={app.id} updateIsDeletingApp={updateIsDeletingApp} />;
        break;
      }
      default:
        break;
    }
    if (!app.mainService?.mainServiceEnvironment?.id && ![MyAppSettingsTab.GENERAL_SETTINGS, MyAppSettingsTab.SUSPEND_DELETE].includes(currentTab)) {
      formContent = <div className="flex flex-col items-center">
        <GhostIconSVG />
        <div className="mt-4 textlg font-sora font-semibold leading-[140%] text-light-grey">
          Nothing here, yet!
        </div>
      </div>
    }
    return <>
      <h3 className="text-light-grey text-lg font-semibold leading140 mb-14">{headerText}</h3>
      {formContent}
    </>
  }

  const handleTriggerDeployment = async () => {
    if (!app) {
      toast.error('App information is not available.', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      dispatch(closeTriggerDeploymentModal());
      return;
    }

    if (!hasWritePermission(app, 'Deploy')) {
      toast.error('You don\'t have permission to trigger deployments.', {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 4000
      });
      dispatch(closeTriggerDeploymentModal());
      return;
    }

    const mainServiceEnvironment = app.mainService && app.mainService.mainServiceEnvironment;
    const mainServiceEnvironmentId = mainServiceEnvironment && mainServiceEnvironment.id;

    try {
      await back4app2.triggerManualDeployment(mainServiceEnvironmentId as string);
      dispatch(closeTriggerDeploymentModal());
      navigate(`/apps/${appId}`);
    } catch (err) {
      let errMsg = 'Something went wrong while triggering new deployment!';
      if (err instanceof NetworkError) {
        errMsg = 'Network error when triggering deployment. Check your internet connection and try again.';
      } else if (err instanceof AuthenticationError) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
        return;
      } else if (err instanceof AppStatusError || err instanceof NotFoundError || err instanceof AuthorizationError) {
        errMsg = err.message;
      } else {
        console.error('error triggering deployment', err);
      }
      toast.error(errMsg, {
        className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
        icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
        duration: 6000
      });
    }
  }

  return (
    <>
      <div className="flex justify-between items-center">
        <h1 className="font-sora text-[1.375rem] leading-140">Settings</h1>
        <div className="text-sm flex gap-2">Web Deployment ID:
          <span className="text-cta-green">{appId}</span>
          <span data-tip="Copied!" data-event='click focus'>
            <CopySVG className="text-cta-green hover:cursor-pointer flex-none" onClick={() => navigator.clipboard.writeText(appId as string)} />
          </span>
        </div>
      </div>
      <div className="pl-[3.75rem]">
        <ul className="w-full flex flex-wrap gap-8 mt-10 text-sm pb-[0.625rem]" role="tablist">
          <li><a href={`#${MyAppSettingsTab.GENERAL_SETTINGS}`} role="tablist" data-toggle="tab" className={`relative duration-300 transition-all before:absolute before:bg-cta-green before:h-[3px] before:w-full before:-bottom-[0.625rem] ${currentTab === MyAppSettingsTab.GENERAL_SETTINGS ? 'text-white before:block' : 'text-[#9ba0a9] before:hidden'}`}>General Settings</a></li>
          <li><a href={`#${MyAppSettingsTab.BUILD_AND_DEPLOY}`} role="tablist" data-toggle="tab" className={`relative duration-300 transition-all before:absolute before:bg-cta-green before:h-[3px] before:w-full before:-bottom-[0.625rem] ${currentTab === MyAppSettingsTab.BUILD_AND_DEPLOY ? 'text-white before:block' : 'text-[#9ba0a9] before:hidden'}`}>Build and Deploy</a></li>
          <li><a href={`#${MyAppSettingsTab.VARIABLES}`} role="tablist" data-toggle="tab" className={`relative duration-300 transition-all before:absolute before:bg-cta-green before:h-[3px] before:w-full before:-bottom-[0.625rem] ${currentTab === MyAppSettingsTab.VARIABLES ? 'text-white before:block' : 'text-[#9ba0a9] before:hidden'}`}>Environment Variables</a></li>
          <li><a href={`#${MyAppSettingsTab.CUSTOM_DOMAIN}`} role="tablist" data-toggle="tab" className={`relative duration-300 transition-all before:absolute before:bg-cta-green before:h-[3px] before:w-full before:-bottom-[0.625rem] ${currentTab === MyAppSettingsTab.CUSTOM_DOMAIN ? 'text-white before:block' : 'text-[#9ba0a9] before:hidden'}`}>Domain</a></li>
          <li><a href={`#${MyAppSettingsTab.HEALTH_AND_ALERT}`} role="tablist" data-toggle="tab" className={`relative duration-300 transition-all before:absolute before:bg-cta-green before:h-[3px] before:w-full before:-bottom-[0.625rem] ${currentTab === MyAppSettingsTab.HEALTH_AND_ALERT ? 'text-white before:block' : 'text-[#9ba0a9] before:hidden'}`}>Health and Alert</a></li>
          {app && app.isOwner && (
            <li><a href={`#${MyAppSettingsTab.SUSPEND_DELETE}`} role="tablist" data-toggle="tab" className={`relative duration-300 transition-all before:absolute before:bg-cta-green before:h-[3px] before:w-full before:-bottom-[0.625rem] ${currentTab === MyAppSettingsTab.SUSPEND_DELETE ? 'text-white before:block' : 'text-[#9ba0a9] before:hidden'}`}>Delete</a></li>
          )}
        </ul>
        <div className="mt-14">
          {isLoadingApp ? <div className="flex h-full justify-center items-center"> <LoadingSpinner color='text-white' /> </div> : null}
          {appLoadingErrorMessage ? (<>
            <div className="flex h-full">
              <ErrorMessage message={appLoadingErrorMessage} />
            </div>
          </>) : (getFormContent())}
        </div>
      </div>
      <Modal open={isTriggerDeploymentModalOpen} onClose={() => dispatch(closeTriggerDeploymentModal())}>
        <>
          <div className="flex w-full justify-center">
            <StatusSuccessSVG className="text-cta-green" width="40px" height="56px" />
          </div>
          <div className="font-semibold leading-140 text-dark text-center mb-2">Settings updated successfully</div>
          <div className="text-sm text-center max-w-xs mb-2">Would you like trigger new deployment with saved settings?</div>
          <div className="flex justify-center space-x-2 w-full mt-4">
            <Button className="text-dark-grey border border-light-grey rounded-[5px]" onClick={() => dispatch(closeTriggerDeploymentModal())}>No</Button>
            <Button 
              type="primary" 
              onClick={handleTriggerDeployment}
              isDisabled={!app || !hasWritePermission(app, 'Deploy')}
            >
              Yes
            </Button>
          </div>
        </>
      </Modal>
      <ReactTooltip
        className="tooltip shadow-[0px_0px_30px_rgba(0,0,0,0.4)]" 
        delayHide={2000} 
        place="top" 
        effect="solid" 
        html={true} 
        arrowColor="#303338" 
        eventOff="click" 
        afterShow={() => navigator.clipboard.writeText(appId as string)}
      />
      
      {/* Paddle Checkout Modal */}
      {openCheckout && (
        <Modal
          open={openCheckout}
          onClose={() => dispatch(setCheckoutModal(false))}
        >
          <div className="w-full max-w-4xl mx-auto">
            <div 
              id="checkout-container" 
              className="checkout-container w-full min-h-[450px] bg-white rounded-lg"
              style={{ minHeight: '450px' }}
            ></div>
          </div>
        </Modal>
      )}
    </>
  )
};

const GeneralSettingsForm = (props: { currentUser: User, appId: string; appName: string, plan?: Plan, plans?: Plan[], isLoadingPlans: boolean, loadingPlansErrorMessage?: string, onPlanClick?: (plan: Plan) => void, isOwner: boolean}) => {
  const { currentUser, appId, appName, plan: currentPlan, isLoadingPlans, loadingPlansErrorMessage, plans, onPlanClick, isOwner } = props;

  let planRAM = '';
  if (currentPlan?.maxRAM) {
    planRAM = currentPlan.maxRAM >= 1024 ? `${currentPlan.maxRAM / 1024}GB` : `${currentPlan.maxRAM}MB`;
  }
  return <form>
    <TextInput value={appName} onChange={() => {}} label="Name" isDisabled={true} />

    <Collaborators currentUser={currentUser} appId={appId} isOwner={isOwner} />

    <div className="mt-6">
      <div className="text-lg text-light-grey leading-140 mb-[0.625rem]">Plan</div>
      <div className="bg-light-grey/[0.06] py-2 px-6 rounded w-full flex justify-between items-center">
        <div className="flex flex-col">
          <span className="uppercase text-lg">{currentPlan?.name}</span>
          <span className="text-sm text-light-blue opacity-70">{planRAM} {currentPlan?.maxCPU} CPU</span>
          <span className="text-sm text-light-grey mt-2">{currentPlan?.tagline}</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-old-blue">$ {currentPlan?.price}.00 / month</span>
          {/* <a role="button" target="_blank" rel='noreferrer noopener' href={`${BACK4APP_DOT_COM_SITE_URL}/pricing/container-as-a-service?appId=${appId}&type=containers`} className="border rounded-[5px] border-cta-green text-cta-green inline-block py-2 px-2.5 text-xs font-medium leading-140">Upgrade</a> */}
        </div>
      </div>
    </div>

    <div className="mt-6">
      <div className="text-lg font-semibold leading-140 mb-2">Choose a new plan</div>
      <div className="text-sm text-light-grey">Compare all plans and choose the perfect fit for your application.</div>
    </div>

    {isLoadingPlans ? <div className="flex h-full justify-center items-center"> <LoadingSpinner color='text-white' /> </div> : <>
      {loadingPlansErrorMessage ? (<>
      <div className="flex h-full">
        <ErrorMessage message={loadingPlansErrorMessage} />
      </div>
    </>) : <div className="mt-6">
        <div className="overflow-x-auto overflow-hidden">
          <table className="w-full">
            <thead className="">
              <tr className="border-b border-regal-blue">
                <th className="p-4 text-left text-sm font-medium text-white/70">Plan</th>
                <th className="p-4 text-left text-sm font-medium text-white/70">CPU</th>
                <th className="p-4 text-left text-sm font-medium text-white/70">RAM</th>
                <th className="p-4 text-left text-sm font-medium text-white/70">Transfer</th>
                <th className="p-4 text-left text-sm font-medium text-white/70">Price</th>
                <th className="p-4 text-left text-sm font-medium text-white/70"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-regal-blue divide-opacity-40 text-xs">
              {plans?.map((plan) => (
                <tr 
                  key={plan.id} 
                  className={`hover:bg-old-blue/10 transition-all cursor-pointer`}
                >
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <span className="text-white font-medium">{plan.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-light-blue">{plan.maxCPU}</td>
                  <td className="p-4 text-light-blue min-w-[100px]">
                    {typeof plan.maxRAM === 'number' 
                      ? plan.maxRAM >= 1024 
                        ? `${(plan.maxRAM / 1024).toFixed(0)} GB`
                        : `${plan.maxRAM} MB`
                      : plan.maxRAM
                    }
                  </td>
                  <td className="p-4 text-light-blue">
                    {typeof plan.transferLimit === 'number'
                      ? plan.transferLimit >= 1000
                        ? `${(plan.transferLimit / 1000).toFixed(0)} TB`
                        : `${plan.transferLimit} GB`
                      : plan.transferLimit
                    }
                  </td>
                  <td className="p-4">
                    <div className="flex flex-col">
                      <span className="text-light-blue font-semibold">
                        ${plan.price}<span className="text-regal-blue font-normal"> / month</span>
                      </span>
                    </div>
                  </td>
                  <td className="p-4">
                    {plan.id === currentPlan?.id ? <Button 
                        type="secondary" 
                        isDisabled={true}
                        className="opacity-30 text-xs cursor-not-allowed rounded-md"
                        onClick={() => {}}
                      >
                        Current Plan
                      </Button> : <Button 
                        type="secondary" 
                        className="text-cta-green border border-cta-green rounded-md text-xs hover:font-normal"
                        onClick={() => onPlanClick?.(plan)}
                      >
                        Change Plan
                      </Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
    </div>}
    </>}
    
  </form>
}

const BuildAndDeployForm = (props: {branchName: string, rootDir: string, autoDeploy: boolean, exposedPort: string, isSubmitting: boolean, handleSubmit: (newValue: AppForm) => {}, plan?: Plan, isFreePlan: boolean, canWrite: boolean, canRead: boolean }) => {
  const { branchName, rootDir, autoDeploy, exposedPort, isSubmitting, handleSubmit, isFreePlan, canWrite, canRead } = props;
  const [formFields, setFormFields] = useState(() => (
    {
      branchName: {
        value: branchName || '',
        isValid: true,
        isRequired: true,
        errorMsg: ''
      },
      rootDir: {
        value: rootDir || '',
        isValid: true,
        isRequired: true,
        errorMsg: ''
      },
      exposedPort: {
        value: exposedPort || '',
        isValid: true,
        isRequired: false,
        errorMsg: ''
      },
      autoDeploy: !!autoDeploy
    }
  ));

  const handleFormFields = (fieldName: MyAppSettingsFieldName, fieldValue: string) => {
    const newState = { ...formFields };
    if (fieldName === MyAppSettingsFieldName.BRANCH_NAME) {
      if (fieldValue === '') {
        newState.branchName.isValid = false;
        newState.branchName.errorMsg = 'Repository branch name is required!'
      } else {
        newState.branchName.errorMsg = '';
        newState.branchName.isValid = true;
      }
      newState.branchName.value = fieldValue;
    } else if (fieldName === MyAppSettingsFieldName.ROOT_DIR) {
      if (!fieldValue.startsWith('./')) {
        if (fieldValue.startsWith('.') || fieldValue.startsWith('/')) {
          fieldValue = fieldValue.substring(1);
        }
        fieldValue = `./${fieldValue}`;
      }
      newState.rootDir.value = fieldValue;
    } else if (fieldName === MyAppSettingsFieldName.EXPOSED_PORT) {
      if (!fieldValue) {
        newState.exposedPort.value = fieldValue;
        return setFormFields({...newState});
      }
      let val = +fieldValue;
      newState.exposedPort.value = fieldValue;
      newState.exposedPort.errorMsg = '';
      newState.exposedPort.isValid = true;
      if ((isNaN(val) || !(Number.isInteger(val) && val > 0 && val <= 65535)) && val !== undefined) {
        newState.exposedPort.errorMsg = 'Please provide a valid TCP port number (1-65535).'
        newState.exposedPort.isValid = false;
        newState.exposedPort.value = ''
      }
    }
    setFormFields({...newState})
  }

  const onSubmit = () => {
    const newValues = {
      branchName: formFields.branchName.value,
      rootDir: formFields.rootDir.value,
      autoDeploy: formFields.autoDeploy,
      exposedPort: +formFields.exposedPort.value
    }
    handleSubmit(newValues);
  }

  if (!canRead) {
    return <GhostMessage customMessage="You don't have permission to access this feature." />;
  }

  return <div className="space-y-6">
    <TextInput 
      value={formFields.branchName.value} 
      label="Branch" 
      name="branch" 
      onChange={(e) => handleFormFields(MyAppSettingsFieldName.BRANCH_NAME, e.target.value.trim())} 
      placeholderText="Branch"
      infoText="The repository branch that your web application uses."
      isError={!formFields.branchName.isValid}
      errorMessage={formFields.branchName.errorMsg}
      isDisabled={isSubmitting || !canWrite}
    />
    <TextInput 
      value={formFields.rootDir.value} 
      label="Root directory" 
      name="rootDirPath" 
      onChange={(e) => handleFormFields(MyAppSettingsFieldName.ROOT_DIR, e.target.value.trim())} 
      placeholderText="./"
      infoText="By default, the root directory is the repository's root. If you specify a different directory, Back4app runs all commands within that directory only and ignores any changes outside it."
      isError={!formFields.rootDir.isValid}
      errorMessage={formFields.rootDir.errorMsg}
      isDisabled={isSubmitting || !canWrite}
    />
    <div className="space-y-2">
      <ToggleSwitchCheckbox 
        value={formFields.autoDeploy} 
        onChange={() => setFormFields(prev => ({...prev, autoDeploy: !prev.autoDeploy}))} 
        labelText="Autodeploy" 
        description="Do you want to automatically deploy on every repository push or whenever your web application changes? Select 'No' to handle deploys manually."
        disabled={isFreePlan || !canWrite}
        infoText={isFreePlan ? "This feature is available on paid plans. Upgrade your plan to automatically deploy your application whenever changes are pushed to your repository.": null}
      />
    </div>
    <TextInput 
      value={`${formFields.exposedPort.value}`} 
      label="Port" 
      name="exposedPort" 
      onChange={(e) => handleFormFields(MyAppSettingsFieldName.EXPOSED_PORT, e.target.value.trim())} 
      placeholderText="8080"
      infoText="By default, the port is set to the one exposed in the Dockerfile. If you specify a different port, Back4app will expose that instead."
      isError={!formFields.exposedPort.isValid}
      errorMessage={formFields.exposedPort.errorMsg}
      isDisabled={!canWrite}
    />
    <div className="mt-12">
      <Button onClick={() => {
        if (!canWrite && canRead) {
          toast.error('You don\'t have permission to save build and deploy settings.', {
            className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
            icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
            duration: 4000
          });
          return;
        }
        onSubmit();
      }} type="primary" className={!canWrite ? 'opacity-50 cursor-not-allowed' : 'py-3 px-[0.875rem]'} isDisabled={isSubmitting}>
        <div className="flex items-center gap-2">
          {isSubmitting ? 'Saving Settings...' : 'Save Settings' }
        </div>
      </Button>
    </div>
  </div>
}

const HealthAndAlertForm = (props: {healthCheckEndpoint: string, isSubmitting: boolean, handleSubmit: (newValue: AppForm) => {}, canWrite: boolean, canRead: boolean }) => {
  const { healthCheckEndpoint, isSubmitting, handleSubmit, canWrite, canRead } = props;
  const [formFields, setFormFields] = useState(() => (
    {
      healthCheckEndpoint: {
        value: healthCheckEndpoint || '',
        isValid: true,
        isRequired: true,
        errorMsg: ''
      }
    }
  ));

  const handleFormFields = (fieldName: MyAppSettingsFieldName, fieldValue: string) => {
    const newState = { ...formFields };
    if (fieldName === MyAppSettingsFieldName.HEALTH_CHECK_ENDPOINT) {
      if (fieldValue[0] !== '/') {
        fieldValue = `/${fieldValue}`
      }
      newState.healthCheckEndpoint.value = fieldValue;
    }
    setFormFields({...newState})
  }

  const onSubmit = () => {
    const newValues = {
      healthCheckEndpoint: formFields.healthCheckEndpoint.value,
    }
    handleSubmit(newValues);
  }

  if (!canRead) {
    return <GhostMessage customMessage="You don't have permission to access this feature." />;
  }

  return <div className="space-y-6">
    <TextInput 
      value={formFields.healthCheckEndpoint.value} 
      label="Health check path" 
      name="healthCheckEndpoint" 
      onChange={(e) => handleFormFields(MyAppSettingsFieldName.HEALTH_CHECK_ENDPOINT, e.target.value.trim())} 
      placeholderText="/checkpath"
      infoText="A health check is an HTTP request performed during deployments to confirm that a service is healthy."
      isError={!formFields.healthCheckEndpoint.isValid}
      errorMessage={formFields.healthCheckEndpoint.errorMsg}
      isDisabled={isSubmitting || !canWrite}
    />
    <div className="mt-12">
      <Button onClick={() => {
        if (!canWrite && canRead) {
          toast.error('You don\'t have permission to save health check settings.', {
            className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
            icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
            duration: 4000
          });
          return;
        }
        onSubmit();
      }} type="primary" className={!canWrite ? 'opacity-50 cursor-not-allowed' : 'py-3 px-[0.875rem]'} isDisabled={isSubmitting}>
        <div className="flex items-center gap-2">
          {isSubmitting ? 'Saving Settings...' : 'Save Settings' }
        </div>
      </Button>
    </div>
  </div>
}

const VariablesForm = (props: { envVars: { id: string, key: string; value?: string }[], isSubmitting: boolean, handleSubmit: (newValues: AppForm) => {}, canWrite: boolean, canRead: boolean }) => {
  const { envVars, isSubmitting, handleSubmit, canWrite, canRead } = props;
  const [formFields, setFormFields] = useState(() => (envVars.map(env => ({
    id: Math.random().toString(16).slice(2), 
    name: { isValid: true, value: env.key, errorMsg: '' }, 
    value: { isValid: true, value: env.value, showValue: false }
  }))));

  const handleOnChangeEnvVars = (id: string, newValue: string, type: 'name' | 'value') => {
    const newEnvVariableList = formFields.map(env => {
      if (id === env.id && type === 'name') {
        // use only uppercase letters, underscore (_) and numbers.
        // Ref: https://stackoverflow.com/a/2821183/11070995
        let isValid = !!newValue.match(/(^[A-Z_])[A-Z0-9_]+$/);
        let errorMsg = '';
        if (!isValid) {
          errorMsg = 'Substitution variables must begin with an underscore (_) or uppercase letter and use only uppercase letters and numbers';
        } else {
          errorMsg = '';
        }
        return { ...env, name: { value: newValue, isValid, errorMsg }}
      } else if (id === env.id && type === 'value') {
        let isValid = newValue !== '';
        return { ...env, value: { ...env.value ,value: newValue, isValid }}
      }
      return env;
    });
    setFormFields([...newEnvVariableList]);
  }

  const handleAddEnviromentVar = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // validate if already empty entry present
    const emptyVarIdx = formFields.findIndex(env => env.name.value === '');
    let newEnvVariableList = [...formFields];
    if (emptyVarIdx !== -1) {
      let newEnvVar = { ...formFields[emptyVarIdx] };
      newEnvVar.name.isValid = false;
      newEnvVar.name.errorMsg = 'A variable name is must!'
      newEnvVariableList.splice(emptyVarIdx, 1, newEnvVar);
    } else {
      const newEnvVar = {
        id: Math.random().toString(16).slice(2), name: { isValid: true, value: '', errorMsg: '' }, value: { isValid: true, value: '', showValue: false }
      } 
      newEnvVariableList = [...formFields, newEnvVar ];
    }
    setFormFields(newEnvVariableList);
  }

  const handleRemoveEnviromentVar = (id: string) => {
    const newEnvVariableList = formFields.filter(env => env.id !== id);
    setFormFields(newEnvVariableList);
  }

  const toggleEnvVariableVisibilty = (id: string) => {
    const newEnvVariableList = formFields.map(env => {
      if (env.id === id) {
        return { ...env, value: {...env.value, showValue: !env.value.showValue} }
      }
      return env;
    });
    setFormFields(newEnvVariableList);
  }

  const onSubmit = () => {
    // validation
    const inValidEnv = formFields.findIndex(env => !env.name.value.match(/(^[A-Z_])[A-Z0-9_]+$/));
    if (formFields.length && inValidEnv !== -1) {
      const newEnv = { ...formFields[inValidEnv] };
      newEnv.name.errorMsg = 'Substitution variables must begin with an underscore (_) or uppercase letter and use only uppercase letters and numbers'
      newEnv.name.isValid = false;
      const newFormFields = formFields.splice(inValidEnv, 1, newEnv);
      setFormFields([...newFormFields]);
      return;
    }
    const envs = formFields.map(env => ({
      key: env.name.value,
      value: env.value.value
    }));
    handleSubmit({ envs });
  }

  if (!canRead) {
    return <GhostMessage customMessage="You don't have permission to access this feature." />;
  }

  return <>
    <div className="text-sm mb-6">Your web application can use these variables at both build time and runtime.</div>
    {formFields.map((enviromentVar) => {
      const { id, name, value } = enviromentVar;
      return(
        <div className="flex justify-between items-start mb-8" key={id}>
          <div className='grid grid-cols-2 gap-x-6 flex-1'>
            <TextInput 
              value={name.value} 
              label="Name" 
              name={`env-name-${id}`} 
              onChange={(e) => handleOnChangeEnvVars(id, e.target.value.trim(), 'name')} 
              placeholderText="EXAMPLE_NAME"
              isError={!name.isValid}
              errorMessage={name.errorMsg}
              isDisabled={!canWrite}
            />
            <TextInput 
              type={value.showValue ? 'text' : 'password'}
              value={value.value || ''} 
              label="Value" 
              name={`env-value-${id}`} 
              onChange={(e) => handleOnChangeEnvVars(id, e.target.value, 'value' )} 
              placeholderText="A1B2C3D4E5F6"
              endAdornment={<span className="cursor-pointer" onClick={() => toggleEnvVariableVisibilty(id)}>{value.showValue ? <VisibilityOffIcon /> : <VisibilityIcon /> }</span>}
              helpText={value.showValue ? "Will be encrypted." : ''}
              isDisabled={!canWrite}
            />
          </div>
          {canWrite ? (
            <span className="text-error-red flex justify-center items-center px-4 border border-error-red rounded hover:cursor-pointer flex-initial ml-4 h-12 mt-[1.9rem] text-center" onClick={() => handleRemoveEnviromentVar(id)}><TrashIcon /></span>
          ) : canRead ? (
            <span className="text-error-red flex justify-center items-center px-4 border border-error-red rounded hover:cursor-pointer flex-initial ml-4 h-12 mt-[1.9rem] text-center opacity-50" onClick={() => {
              toast.error('You don\'t have permission to delete environment variables.', {
                className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
                icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
                duration: 4000
              });
            }}><TrashIcon /></span>
          ) : null}
        </div>
      )}
    )}
    {/* Add button for Environment varible */}
    {canWrite ? (
      <a onClick={handleAddEnviromentVar} type="button" href="/#" className="inline-flex text-cta-green text-sm font-semibold leading-140 items-center"> <PlusIcon className="mr-[0.625rem]" /> Add variable</a>
    ) : canRead ? (
      <a onClick={(e) => {
        e.preventDefault();
        toast.error('You don\'t have permission to add environment variables.', {
          className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
          icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
          duration: 4000
        });
      }} type="button" href="/#" className="inline-flex text-cta-green text-sm font-semibold leading-140 items-center opacity-50"> <PlusIcon className="mr-[0.625rem]" /> Add variable</a>
    ) : null}
    <div className="mt-12">
      <Button onClick={() => {
        if (!canWrite && canRead) {
          toast.error('You don\'t have permission to save environment variables.', {
            className:"bg-white px-6 py-4 text-dark text-center rounded-none rounded-bl-lg rounded-br-lg shadow-[0_6px_16px_rgba(0,0,0,0.25)] max-w-xs text-sm",
            icon: <StatusErrorSVG width="24px" height="24px" className="animate-bounce-in" />,
            duration: 4000
          });
          return;
        }
        onSubmit();
      }} type="primary" className={!canWrite ? 'opacity-50 cursor-not-allowed' : 'py-3 px-[0.875rem]'} isDisabled={isSubmitting}>
        <div className="flex items-center gap-2">
          {isSubmitting ? 'Saving Settings...' : 'Save Settings' }
        </div>
      </Button>
    </div>
  </>
};

const SuspendDeleteForm = (props: { appName: string, appId: string, updateIsDeletingApp: (isDeleting: boolean) => void }) => {
  const { appName, appId, updateIsDeletingApp } = props;
  const [openModal, setOpenModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteAppErrorMessage, setDeleteAppErrorMessage] = useState('');
  const [verifyAppNameInput, setVerifyAppNameInput] = useState(() => ({
    value: '',
    isError: false,
    isValid: true,
    isTouched: false,
    errorMsg: ''
  }));

  const onChangeDeleteAppInput = (val: string) => {
    let isValid=true, errorMsg='';
    if (val !== appName) {
      isValid = false; errorMsg = 'App name entered does not match!';
    }
    setVerifyAppNameInput({
      ...verifyAppNameInput,
      value: val,
      isValid,
      errorMsg,
      isTouched: true
    });
  };

  const handleDeleteApp = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (!verifyAppNameInput.isTouched) {
      return;
    }
    setIsDeleting(true);
    updateIsDeletingApp(true);
    trackEvent(AmplitudeEvent.CLICK_ON_DELETE_APP);
    try {
      await back4app2.deleteApp(appId as string);
      updateIsDeletingApp(false);
      window.location.href = `${BACK4APP_DOT_COM_DASHBOARD_URL}/apps`;
    } catch (err) {
      if (err instanceof NetworkError) {
        setDeleteAppErrorMessage('Network error when deleting app. Please check your internet connection or try again later.');
      } else if (err instanceof AuthenticationError || err instanceof AuthorizationError) {
        window.location.replace(`${BACK4APP_DOT_COM_SITE_URL}/login?return-url=${encodeURIComponent(window.location.href)}`);
      } else {
        console.error('error deleting app', err);
        setDeleteAppErrorMessage('Something went wrong, please try again later.');
      }
    } finally {
      setIsDeleting(false);
    }
  }

  return <div className="flex space-y-6 flex-col">
    <div className="flex flex-col justify-start items-start">
      <div className="font-medium mb-[0.5rem]">Delete</div>
      <div className="text-sm mb-[1rem]">All resources for your web application will stop working immediately, and this action cannot be undone.</div>
      <button onClick={(e) => setOpenModal(true)} className="inline-flex items-center outline-none border border-regal-blue rounded-[5px] bg-transparent font-bold px-[0.625rem] py-[0.5rem]"><TrashOutlineIcon className="text-error-red mr-[0.625rem]" />Delete</button>
    </div>

    <Modal open={openModal} onClose={() => setOpenModal(false)}>
      <>
        <div className="flex w-full justify-center">
          <TriangleExclamationIcon className="text-error-red" width="40px" height="56px" />
        </div>
        <div className="font-semibold leading-140 text-dark text-center mb-2">Delete the web application?</div>
        <TextInput 
          value={verifyAppNameInput.value} 
          label="Enter the web application name below to confirm." 
          name="confirmDeleteAppName" 
          onChange={(e) => onChangeDeleteAppInput(e.target.value.trim())} 
          placeholderText="Web application name"
          isError={!verifyAppNameInput.isValid}
          errorMessage={verifyAppNameInput.errorMsg}
          className="border border-light-grey border-opacity-100 w-full p-3 text-dark"
          labelClassName="font-normal text-dark opacity-70 text-sm justify-center normal-case"
          isLightThemed={true}
        />
        {deleteAppErrorMessage ? <span className='text-xs text-error-red'>{deleteAppErrorMessage}</span> : null}
        <div className="flex justify-end space-x-2 w-full mt-4">
          <Button className="text-dark-grey border border-light-grey rounded-[5px]" onClick={() => setOpenModal(false)}>Cancel</Button>
          <Button type="primary" isDisabled={!verifyAppNameInput.isTouched || !verifyAppNameInput.isValid} onClick={handleDeleteApp}>{isDeleting ? 'Deleting...' : 'Delete'}</Button>
        </div>
      </>
    </Modal>

  </div>
}

export default MyAppSettings